import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { AccountSwitcherDropdown, ProfileButton, type AccountsState } from '../../accounts';
import { DemoBanner } from '../shared/DemoBanner';
import { StorageWarningBanner } from '../shared/StorageWarningBanner';
import { FloatingDjay } from '../../mascot';
import { AchievementCeremony } from '../shared/AchievementCeremony';
import { ConnectionStatus } from '../shared/ConnectionStatus';
import { InstallAppPrompt } from '../shared/InstallAppPrompt';
import { Menu } from 'lucide-react';
import { APP_NAME } from '../../config/constants';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDirectionalTransition, getTransitionVariants } from '../../app/hooks/useDirectionalTransition';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface AppShellProps {
  children: React.ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
  accounts?: AccountsState;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
  onResetDemo?: () => void;
}
export function AppShell({
  children,
  currentView,
  onNavigate,
  accounts,
  onSwitchAccount,
  onLogout,
  onResetDemo
}: AppShellProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const {
    isDemoMode
  } = useAppStore();
  // Auto-collapse sidebar on tablet screens (768px - 1023px)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setIsSidebarCollapsed(true);
      }
    };
    // Initial check
    handleResize(mediaQuery);
    // Listen for changes
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);
  // Track if this is the first render to skip the entry animation
  const isFirstRender = useRef(true);
  const skipInitial = isFirstRender.current;
  if (isFirstRender.current) {
    isFirstRender.current = false;
  }
  // Convert AccountsState to array of accounts
  const accountsList = accounts ? Object.values(accounts.accountsById) : [];
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts ? accounts.accountsById[activeAccountId] : null;
  const direction = useDirectionalTransition(currentView);
  const {
    isReducedMotion
  } = useMotionBudget();
  const transitionVariants = getTransitionVariants(direction, isReducedMotion);
  return <div className="min-h-screen bg-[#FAFAF8] flex flex-col font-sans text-slate-900" data-id="element-1478">
      {/* Demo Banner */}
      {isDemoMode && onResetDemo && <DemoBanner onResetDemo={onResetDemo} onRegister={() => onNavigate('register')} data-id="element-1479" />}

      {/* Storage Warning Banner (RES-4) */}
      <StorageWarningBanner data-id="element-1480" />

      <div className="flex-1 flex overflow-hidden" data-id="element-1481">
        {/* Skip-to-content link for keyboard accessibility */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-300 font-semibold text-sm" data-id="element-1482">
          Ga naar inhoud
        </a>

        {/* Desktop Sidebar */}
        <Sidebar currentView={currentView} onNavigate={onNavigate} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-1483" />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative" data-id="element-1484">
          {/* Mobile Header */}
          <header className="md:hidden h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-5 sticky top-0 z-30 shadow-sm" data-id="element-1485">
            <div className="flex items-center gap-3" data-id="element-1486">
              <button onClick={() => setIsMobileNavOpen(true)} className="p-3 -ml-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-orange-300" aria-label="Menu openen" data-id="element-1487">
                <Menu className="w-6 h-6" data-id="element-1488" />
              </button>
              <span className="font-black text-slate-800 text-lg tracking-tight" data-id="element-1489">
                {APP_NAME}
              </span>
            </div>

            <div className="flex items-center gap-3" data-id="element-1490">
              {/* Mobile Account Switcher / Profile Button */}
              {isDemoMode ? accountsList.length > 0 && onSwitchAccount && <AccountSwitcherDropdown accounts={accountsList} activeAccountId={activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-1491" /> : activeAccount && onLogout && <ProfileButton account={activeAccount} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1492" />}
            </div>
          </header>

          {/* Desktop Header */}
          <header className="hidden md:flex h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 items-center justify-end px-8 sticky top-0 z-30 shadow-sm" data-id="element-1493">
            {isDemoMode ? <div className="flex items-center gap-3" data-id="element-1494">
                <button onClick={() => onNavigate('register')} className="flex items-center gap-1.5 px-4 py-2 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-xl text-sm font-bold transition-colors" data-id="element-1495">
                  🧪 Start Registratie
                </button>
                {accountsList.length > 0 && onSwitchAccount && <AccountSwitcherDropdown accounts={accountsList} activeAccountId={activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-1496" />}
              </div> : activeAccount && onLogout && <ProfileButton account={activeAccount} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1497" />}
          </header>

          {/* Scrollable Content */}
          <main id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10 scroll-smooth" data-id="element-1498">
            <div className="max-w-6xl mx-auto w-full pb-24 md:pb-12" data-id="element-1499">
              <AnimatePresence mode="wait" data-id="element-1500">
                <motion.div key={currentView} initial={skipInitial ? false : transitionVariants.initial} animate={transitionVariants.animate} exit={transitionVariants.exit} transition={{
                duration: 0.25,
                ease: [0.25, 0.1, 0.25, 1] // Custom cubic-bezier for smoother feel
              }} className="w-full" data-id="element-1501">
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>

        {/* Mobile Navigation Drawer */}
        <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} currentView={currentView} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1502" />
      </div>

      {/* Floating Djay Buddy */}
      <FloatingDjay currentView={currentView} onNavigate={onNavigate} data-id="element-1503" />

      {/* Global Achievement Ceremony */}
      <AchievementCeremony onNavigateToCollection={() => onNavigate('collection')} data-id="element-1504" />

      {/* F5.6 — Offline Indicator */}
      <ConnectionStatus data-id="element-1505" />

      {/* PWA Install Prompt — verschijnt wanneer Chrome/Edge installatie aanbiedt */}
      <InstallAppPrompt data-id="element-1506" />
    </div>;
}